import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { ResolvedColumn } from '../../domain';
import { storableFilters } from '../../domain/query/locked-filters';
import {
    filterParamName,
    parseFilters,
    parseSort,
    searchParamName,
    serializeFilters,
    serializeSort,
    sortParamName,
} from '../../domain/query/url-params';
import type { ResolvedFilter } from '../../domain/query/filter';
import type { SortState } from '../../domain/query/sort';
import type { AdapterSnapshot, TableAdapter, UrlAdapter } from '../../ports';

function serializedKey(
    filters: ResolvedFilter[],
    sort: SortState | null,
    search: string,
): string {
    return `${serializeFilters(filters)}|${sort ? serializeSort(sort) : ''}|${search}`;
}

export interface UseUrlStateArgs<T> {
    id: string;
    adapter: TableAdapter<T>;
    columns: ResolvedColumn<T>[];
    snapshot: AdapterSnapshot<T>;
    url: UrlAdapter | false;
    userFilters?: MutableRefObject<ResolvedFilter[]>;
}

export function useUrlState<T>(args: UseUrlStateArgs<T>): void {
    const { id, adapter, columns, snapshot, url, userFilters } = args;
    const hydrated = useRef<TableAdapter<T> | null>(null);
    const lastSynced = useRef<string | null>(null);

    if (url !== false && hydrated.current !== adapter) {
        hydrated.current = adapter;
        lastSynced.current = null;
        const params = new URLSearchParams(url.getSearch());

        const rawFilters = params.get(filterParamName(id));
        if (rawFilters) {
            const filters = parseFilters(rawFilters, columns);
            if (filters.length > 0) adapter.setFilters(filters);
        }

        const rawSort = params.get(sortParamName(id));
        if (rawSort) {
            const sort = parseSort(rawSort, columns);
            if (sort && (adapter.canSortBy?.(sort.key) ?? true))
                adapter.setSort(sort);
        }

        const rawSearch = params.get(searchParamName(id));
        if (rawSearch) adapter.setSearch(rawSearch);
    }

    if (url !== false && lastSynced.current === null) {
        lastSynced.current = serializedKey(
            storableFilters(userFilters?.current, snapshot.filters),
            snapshot.sort,
            snapshot.search,
        );
    }

    useEffect(() => {
        if (url === false) return;

        const filtersToSync = storableFilters(
            userFilters?.current,
            snapshot.filters,
        );
        const serialized = serializedKey(
            filtersToSync,
            snapshot.sort,
            snapshot.search,
        );

        if (serialized === lastSynced.current) return;
        lastSynced.current = serialized;

        const current = url.getSearch();
        const params = new URLSearchParams(current);

        if (filtersToSync.length > 0) {
            params.set(filterParamName(id), serializeFilters(filtersToSync));
        } else {
            params.delete(filterParamName(id));
        }

        if (snapshot.sort) {
            params.set(sortParamName(id), serializeSort(snapshot.sort));
        } else {
            params.delete(sortParamName(id));
        }

        if (snapshot.search) {
            params.set(searchParamName(id), snapshot.search);
        } else {
            params.delete(searchParamName(id));
        }

        const next = params.toString();
        if (next !== new URLSearchParams(current).toString())
            url.replaceSearch(next);
    }, [
        id,
        url,
        snapshot.filters,
        snapshot.sort,
        snapshot.search,
        userFilters,
    ]);
}
